import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HitApi } from '../../../Api/ApiHit';
import { searchMaintenanceMode, deleteMaintenanceMode, toggleMaintenanceMode, searchUnit, updateUnit } from '../../../constant/Constant';
import { Plus, Trash2, Pencil, ShieldAlert, Power, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const MaintenanceModeMaster = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Unit-level maintenance
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitMessage, setUnitMessage] = useState('');

  useEffect(() => {
    fetchRecords();
    fetchUnits();
  }, [page]);

  const fetchRecords = () => {
    setLoading(true);
    const json = {
      page,
      limit,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };
    HitApi(json, searchMaintenanceMode)
      .then((res) => {
        if (res?.data?.docs) {
          setRecords(res.data.docs);
          setTotalPages(res.data.totalPages || 1);
        } else if (res?.data && Array.isArray(res.data)) {
          setRecords(res.data);
        }
      })
      .catch((err) => {
        console.error('Error fetching maintenance modes:', err);
        toast.error('Failed to fetch maintenance records');
      })
      .finally(() => setLoading(false));
  };

  const fetchUnits = () => {
    setUnitsLoading(true);
    HitApi({ page: 1, limit: 1000, search: {} }, searchUnit)
      .then((res) => {
        if (res?.data?.docs) {
          setUnits(res.data.docs);
        } else if (res?.data && Array.isArray(res.data)) {
          setUnits(res.data);
        }
      })
      .catch(() => toast.error('Failed to fetch units'))
      .finally(() => setUnitsLoading(false));
  };

  const handleDelete = () => {
    if (!deleteModal.id) return;
    HitApi({ _id: deleteModal.id }, deleteMaintenanceMode)
      .then((res) => {
        if (res?.statusCode === 200 || res?.success) {
          toast.success('Maintenance record deleted successfully');
          fetchRecords();
        } else {
          toast.error(res?.message || 'Failed to delete');
        }
      })
      .catch(() => toast.error('Failed to delete'))
      .finally(() => setDeleteModal({ open: false, id: null }));
  };

  const handleToggle = (record) => {
    const newState = !record.isEnabled;
    HitApi({ _id: record._id, isEnabled: newState }, toggleMaintenanceMode)
      .then((res) => {
        if (res?.statusCode === 200 || res?.success) {
          toast.success(`Maintenance mode ${newState ? 'enabled' : 'disabled'}`);
          fetchRecords();
        } else {
          toast.error(res?.message || 'Failed to toggle');
        }
      })
      .catch(() => toast.error('Failed to toggle maintenance mode'));
  };

  const handleUnitToggle = (unit) => {
    const newState = !unit.appMaintenanceMode;
    if (newState && !unit.appMaintenanceMessage) {
      setEditingUnit(unit._id);
      setUnitMessage(unit.appMaintenanceMessage || '');
      return;
    }
    HitApi({ _id: unit._id, appMaintenanceMode: newState }, updateUnit)
      .then((res) => {
        if (res?.statusCode === 200 || res?.success) {
          toast.success(`${unit.unitName} maintenance ${newState ? 'enabled' : 'disabled'}`);
          fetchUnits();
        } else {
          toast.error(res?.message || 'Failed to update');
        }
      })
      .catch(() => toast.error('Failed to update unit'));
  };

  const handleUnitMessageSave = (unitId) => {
    HitApi({ _id: unitId, appMaintenanceMode: true, appMaintenanceMessage: unitMessage }, updateUnit)
      .then((res) => {
        if (res?.statusCode === 200 || res?.success) {
          toast.success('Unit maintenance enabled');
          setEditingUnit(null);
          setUnitMessage('');
          fetchUnits();
        } else {
          toast.error(res?.message || 'Failed to update');
        }
      })
      .catch(() => toast.error('Failed to update unit'));
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Maintenance Mode</h1>
          <p className="text-sm text-gray-500 mt-1">Manage application and unit-level maintenance</p>
        </div>
        <button
          onClick={() => navigate('/app-control/maintenance/add')}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add App Maintenance
        </button>
      </div>

      {/* ==================== APP-LEVEL SECTION ==================== */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5" />
          App-Level Maintenance
        </h2>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          </div>
        )}

        {!loading && records.length === 0 && (
          <div className="text-center py-8 bg-white rounded-lg shadow border border-gray-100">
            <ShieldAlert className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No app-level maintenance records</p>
          </div>
        )}

        {!loading && records.length > 0 && (
          <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Title</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Message</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Start Time</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">End Time</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Enabled By</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {records.map((record) => (
                    <tr key={record._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900 text-sm">{record.title}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 truncate block max-w-[200px]">{record.message}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${record.isEnabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {record.isEnabled ? 'Under Maintenance' : 'Normal'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(record.startTime)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(record.endTime)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{record.enabledBy || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggle(record)}
                            title={record.isEnabled ? 'Disable maintenance' : 'Enable maintenance'}
                            className={`p-1.5 rounded-lg transition-colors ${record.isEnabled ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/app-control/maintenance/edit/${record._id}`)}
                            className="p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            <Pencil className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => setDeleteModal({ open: true, id: record._id })}
                            className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== UNIT-LEVEL SECTION ==================== */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Unit-Level Maintenance
        </h2>
        <p className="text-sm text-gray-500 mb-4">Toggle maintenance for individual units. When enabled, users assigned to that unit will see maintenance screen in the app.</p>

        {unitsLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          </div>
        )}

        {!unitsLoading && units.length === 0 && (
          <div className="text-center py-8 bg-white rounded-lg shadow border border-gray-100">
            <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No units found</p>
          </div>
        )}

        {!unitsLoading && units.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {units.map((unit) => (
              <div key={unit._id} className="bg-white rounded-lg shadow border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{unit.unitName}</h3>
                    <p className="text-xs text-gray-500">{unit.unitCode}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${unit.appMaintenanceMode ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {unit.appMaintenanceMode ? 'Maintenance' : 'Live'}
                  </span>
                </div>

                <p className="text-xs text-gray-500 mb-3 truncate">{unit.address}</p>

                {unit.appMaintenanceMode && unit.appMaintenanceMessage && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mb-3">{unit.appMaintenanceMessage}</p>
                )}

                {/* Editing message inline */}
                {editingUnit === unit._id && (
                  <div className="mb-3">
                    <input
                      type="text"
                      value={unitMessage}
                      onChange={(e) => setUnitMessage(e.target.value)}
                      placeholder="Maintenance message for this unit"
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent mb-2"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUnitMessageSave(unit._id)}
                        className="text-xs bg-black text-white px-3 py-1 rounded-lg hover:bg-gray-800"
                      >
                        Enable
                      </button>
                      <button
                        onClick={() => { setEditingUnit(null); setUnitMessage(''); }}
                        className="text-xs border border-gray-300 px-3 py-1 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {editingUnit !== unit._id && (
                  <button
                    onClick={() => handleUnitToggle(unit)}
                    className={`w-full flex items-center justify-center gap-2 text-xs font-medium py-2 rounded-lg transition-colors ${
                      unit.appMaintenanceMode
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    <Power className="w-3.5 h-3.5" />
                    {unit.appMaintenanceMode ? 'Disable Maintenance' : 'Enable Maintenance'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Maintenance Record</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this maintenance record? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModal({ open: false, id: null })}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceModeMaster;
